import { useMemo } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { Flex, Table, Typography } from 'antd';
import type { TableColumnsType } from 'antd';

import { usePagination } from '@/shared/hooks/usePagination';
import { EMPTY_MESSAGES, OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { parseFqnToPathParts } from '@/shared/lib/format/fqn';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList/BadgeList';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useDisplaySettingsStore } from '@/entities/display-settings';

import { resolveGlossaryTermListRowHref } from '@/features/glossary/lib/resolveGlossaryTermListRowHref';
import { useChildTerms } from '@/features/glossary/model';

import styles from './GlossaryTermsTab.module.css';

const { Text } = Typography;

type TableRowData = {
  key: string;
  displayName: string;
  fullyQualifiedName: string;
  description?: string;
  synonyms?: string[];
  relatedTerms?: Array<{
    id: string;
    name: string;
    displayName: string;
    fullyQualifiedName: string;
  }>;
  owners?: Array<{ name: string }>;
  childrenCount?: number;
};

type GlossaryTermsTabProps = {
  termFqn: string;
};

export const GlossaryTermsTab = ({ termFqn }: GlossaryTermsTabProps) => {
  const navigate = useNavigate();
  const { data: childTermsData, isLoading } = useChildTerms(termFqn);
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const termCategory = parseFqnToPathParts(termFqn)[0];

  const dataSource = useMemo<TableRowData[]>(() => {
    if (!childTermsData?.data) return [];
    return childTermsData.data
      .filter((term) => term.status === 'Approved')
      .map((term) => ({
        key: term.id,
        displayName: term.displayName || term.name,
        fullyQualifiedName: term.fullyQualifiedName,
        description: term.description,
        synonyms: term.synonyms,
        relatedTerms: term.relatedTerms,
        owners: term.owners,
        childrenCount: term.childrenCount,
      }));
  }, [childTermsData]);

  const { paginationConfig } = usePagination({
    dataLength: dataSource.length,
    viewportOffset: 548,
  });

  const hasData = dataSource.length > 0;

  const columns: TableColumnsType<TableRowData> = useMemo(() => {
    const cols: TableColumnsType<TableRowData> = [
      {
        title: '용어명',
        dataIndex: 'displayName',
        key: 'displayName',
        width: '20%',
        render: (text: string, record: TableRowData) => (
          <Link
            className={styles.termNameLink}
            to={resolveGlossaryTermListRowHref(termCategory, record.fullyQualifiedName)}>
            <EllipsisText text={text} tooltipPlacement="topLeft" />
          </Link>
        ),
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        render: (description?: string) => <AssetDescription description={description} mode="expandable" />,
        width: hasData ? 450 : 'auto',
      },
      {
        title: '동의어',
        dataIndex: 'synonyms',
        key: 'synonyms',
        width: 150,
        render: (synonyms?: string[]) => {
          if (!synonyms || synonyms.length === 0) return <Text type="secondary">-</Text>;
          const badgeItems = synonyms.map((synonym) => ({
            tagFQN: synonym,
            name: synonym,
            displayName: synonym,
          }));
          return <BadgeList items={badgeItems} iconName="book" maxVisibleCount={3} />;
        },
      },
      {
        title: '관련 용어',
        dataIndex: 'relatedTerms',
        key: 'relatedTerms',
        width: 200,
        render: (relatedTerms?: TableRowData['relatedTerms']) => {
          if (!relatedTerms || relatedTerms.length === 0) return '-';
          const badgeItems = relatedTerms.map((term) => ({
            tagFQN: term.fullyQualifiedName,
            name: term.name,
            displayName: term.displayName,
          }));
          return (
            <BadgeList
              maxVisibleCount={5}
              iconName="book"
              items={badgeItems}
              onItemClick={(fqn) => {
                navigate(buildAssetDetailUrl('glossaryTerm', fqn));
              }}
            />
          );
        },
      },
    ];

    if (showOwners) {
      cols.push({
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: 150,
        render: (owners?: Array<{ name: string }>) => {
          if (!owners || owners.length === 0) return '-';
          const formattedOwners = owners.map((owner) => ({
            id: owner.name,
            name: owner.name,
            displayName: owner.name,
            type: 'user' as const,
          }));
          return <OwnerAvatars owners={formattedOwners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      });
    }

    return cols;
  }, [navigate, hasData, showOwners, termCategory]);

  if (isLoading) {
    return <LoadingState message="하위 용어를 불러오는 중..." />;
  }

  return (
    <NoDataBoundary data={dataSource} emptyDescription={EMPTY_MESSAGES.NO_GLOSSARY}>
      <Flex vertical className={styles.termsTab}>
        <Table<TableRowData>
          columns={columns}
          dataSource={dataSource}
          pagination={paginationConfig}
          scroll={{ x: dataSource.length > 0 ? 'max-content' : undefined }}
          className={styles.termsTable}
        />
      </Flex>
    </NoDataBoundary>
  );
};
